<script setup>
    const props = defineProps({
        image: {
            type: Object,
            required: false,
            default: {},
        },
        src: {
            type: String,
            required: false,
            default: null,
        },
        alt: {
            type: String,
            required: false,
            default: null,
        },
        lazy: {
            type: Boolean,
            required: false,
            default: true,
        },
        // This default is what every call site gets — nothing in the app passes
        // an explicit quality — so it, not nuxt.config's `quality`, is the
        // number that reaches Cloudflare. nuxt-img resolves
        // `props.quality || config.quality`, and because this prop always has
        // a value the config half was never consulted.
        //
        // 75: q=50 came back visibly soft (Asana O2-79) and was raised to 90,
        // but at 90 a full-width hero ran ~515KB of AVIF on a phone. 75 is the
        // usual knee for AVIF/WebP — most of the saving without O2-79's
        // softness, now that unsized images are also requested at the width
        // they display at (see effectiveSizes below) rather than at 1920.
        quality: {
            type: Number,
            required: false,
            default: 75,
        },
        forceLoad: {
            type: Boolean,
            required: false,
            default: false,
        },
        height: {
            type: [Number, Boolean, String],
            required: false,
            default: false,
        },
        width: {
            type: [Number, Boolean, String],
            required: false,
            default: false,
        },
        mod: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        sizes: {
            type: String,
            required: false,
            default: '',
        },
        isTransparent: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Declared so it is NOT passed through to <nuxt-img>, and ignored.
        // Call sites pass a bare `placeholder`, which made nuxt-img render a
        // 10x10 blurred image on the server and only set the real src after
        // hydration — the real image could not start downloading until the JS
        // had run (the LCP on site pages), and nuxt-img then fetched it eagerly
        // via `new Image()` on mount, ignoring loading="lazy", so every card
        // on /sites downloaded at once. It also put a second, throwaway entry
        // per photo into the service worker's image cache.
        placeholder: {
            type: [Boolean, String, Number, Array],
            required: false,
            default: undefined,
        },
        // Declared (rather than left to fall through) so the priority rule
        // below can see it. Same precedence as before: an explicit `loading`
        // wins over `lazy`.
        loading: {
            type: String,
            required: false,
            default: undefined,
        },
        // The page's main image (LCP): loaded eagerly at high fetch priority
        // and preloaded from the document head.
        priority: {
            type: Boolean,
            required: false,
            default: false,
        },
    });

    const loadingValue = props.loading || (props.lazy ? 'lazy' : 'eager');

    // Setup URL
    let url = props.image?.r2Url || props.image?.src || props.image?.url || props.image?.image || props.src;

    // Define URLs to remove:
    // NuxtImage should resolve the correct URL for the environment
    // based on the providers base URL
    const config = useRuntimeConfig();
    const { imageUrlsToRemove } = config.public;
    const urlsToRemove = imageUrlsToRemove.split(',');

    // Check if the URL contains any of the URLs to remove
    const shouldRemove = urlsToRemove.some((url) => {
        return props.image?.src?.includes(url) || props.image?.url?.includes(url) || props.image?.image?.includes(url) || props.src?.includes(url);
    });

    if (shouldRemove) {
        // Remove the URL from the image src
        urlsToRemove.forEach((aUrl) => {
            url = url.replace(aUrl, '');
        });
    }

    const urlsToProxy = ['v5.airtableusercontent.com'];
    const proxyUrl = 'images.architecture.org';
    const shouldProxy = urlsToProxy.some((url) => {
        let shouldProxy = false;
        shouldProxy = shouldProxy || props.image?.url?.includes(url);
        shouldProxy = shouldProxy || props.image?.src?.includes(url);
        shouldProxy = shouldProxy || props.src?.includes(url);
        return shouldProxy;
    });

    if (shouldProxy) {
        urlsToProxy.forEach((aUrl) => {
            url = url.replace(aUrl, proxyUrl);
        });
    }

    const lowerCase = url?.toLowerCase() || '';
    const transparent = lowerCase?.includes('.png') || lowerCase?.includes('.svg') || lowerCase?.includes('.gif') || props.isTransparent;
    const shouldPreload = props.lazy && !props.forceLoad && !transparent;

    const hasImage = props.image || props.src;

    // No width and no `sizes` used to mean a single fixed w=1920 request, so
    // a phone downloaded a desktop-sized hero. Full-bleed `sizes` lets the
    // browser pick a width for the viewport instead; 100vw is the upper bound
    // of any slot, so nothing is ever served short. Photos only: logos and
    // other transparent PNG/SVG art keep their previous single request.
    const effectiveSizes = props.sizes || (props.width || transparent ? '' : SIZES_FULL_BLEED);

    // An eager, unsized image is a hero (the site page's first carousel
    // slide, the full-bleed banner) — the page's LCP. Small eager thumbnails
    // pass a width and are left alone.
    const isPriority = props.priority || (loadingValue === 'eager' && !props.width);

    // width/height/quality/format/fit have to go on <nuxt-img> as PROPS — inside
    // `modifiers` they are silently overwritten by the component's own props.
    // See app/utils/imageModifiers.ts for the mechanism and the measurements.
    const img = buildImageBindings({
        width: props.width,
        height: props.height,
        quality: props.quality,
        sizes: effectiveSizes,
        mod: props.mod,
    });

    // `alt` must always be present. An explicit alt="" marks the image as
    // decorative and has to survive (`||` turned it into no attribute at
    // all), and with no alt given anywhere an empty one is still better than
    // none, which screen readers announce as the file name.
    const altText = computed(() => props.alt ?? props.image?.alt ?? '');

    // Falls back to a local placeholder when a remote (r2Url/Airtable) photo
    // 404s or is unreachable offline. NuxtImg renders a native <img> under the
    // hood, but its @error payload isn't always a real DOM event (its own
    // SSR-hydration mismatch path emits a bare `new Event('error')` with
    // `target: null`), so we drive the src off reactive state rather than
    // reaching into event.target.
    const FALLBACK_IMAGE = '/img/default.png';
    const resolvedUrl = ref(url);
    const hasErrored = ref(false);

    function onImageError() {
        if (hasErrored.value) return;
        hasErrored.value = true;
        resolvedUrl.value = FALLBACK_IMAGE;
    }
</script>

<template>
    <nuxt-img
        v-if="hasImage && shouldPreload && url !== null"
        v-bind="img"
        :loading="isPriority ? 'eager' : loadingValue"
        :fetchpriority="isPriority ? 'high' : undefined"
        :preload="isPriority ? { fetchPriority: 'high' } : undefined"
        :src="resolvedUrl"
        :alt="altText"
        class="full-sized-image"
        :sizes="effectiveSizes || undefined"
        @error="onImageError" />
    <nuxt-img
        v-else-if="hasImage && !shouldPreload && url !== null"
        v-bind="img"
        loading="eager"
        :fetchpriority="isPriority ? 'high' : undefined"
        :preload="isPriority ? { fetchPriority: 'high' } : undefined"
        :src="resolvedUrl"
        :alt="altText"
        :sizes="effectiveSizes || undefined"
        @error="onImageError" />
</template>
